import { create } from "zustand";
import type {
	FeedbackFlowState,
	FeedbackData,
	ConsentType,
	UserInfo,
	CompanyInfo,
	QuestionAnswer,
	FlowStep,
	FlowType,
} from "../types";
import type { ApiPage, ApiQuestion } from "../types/api.types";
import { feedbackApi } from "../services/api.service";
import { useFormConfigStore } from "./form-config-store";

const initialUserInfo: UserInfo = {
	customer_name: "",
	customer_email: "",
	customer_phone: "",
	customer_headline: "",
	photoFile: null,
	photoPreview: null,
};

const initialCompanyInfo: CompanyInfo = {
	customer_job_title: "",
	customer_company_name: "",
	customer_company_website_url: "",
	customer_company_logo_url: "",
	logoFile: null,
	logoPreview: null,
};

const initialData: FeedbackData = {
	rating: 0,
	contentText: "",
	consent: null,
	userInfo: initialUserInfo,
	companyInfo: initialCompanyInfo,
	questionAnswers: {},
};

// Page types for positive flow (>= threshold stars)
const POSITIVE_FLOW_PAGE_TYPES = [
	"rating",
	"welcome",
	"about_user",
	"about_company",
	"consent",
	"thank_you",
];

// Page types for negative flow (< threshold stars)
const NEGATIVE_FLOW_PAGE_TYPES = ["rating", "private_feedback", "negative_thank_you"];

/**
 * Build the flow steps from pages and questions.
 * For positive flow, question steps are inserted after the "welcome" page.
 * Pages with status === 'disabled' are skipped.
 */
function buildFlowSteps(
	pages: ApiPage[],
	questions: ApiQuestion[],
	flowType: FlowType,
): FlowStep[] {
	const pageTypes =
		flowType === "positive"
			? POSITIVE_FLOW_PAGE_TYPES
			: NEGATIVE_FLOW_PAGE_TYPES;

	// Filter to relevant pages, skip disabled, and sort by flow order
	const filteredPages = pages
		.filter(
			(p) => pageTypes.includes(p.type) && (p.status ?? "enabled") !== "disabled"
		)
		.sort((a, b) => pageTypes.indexOf(a.type) - pageTypes.indexOf(b.type));

	const steps: FlowStep[] = filteredPages.map((page) => ({ page }));

	// For positive flow, insert question steps after "welcome" if present,
	// otherwise after "rating" so questions still appear when welcome is disabled.
	if (flowType === "positive" && questions.length > 0) {
		const welcomeIndex = steps.findIndex((step) => step.page.type === "welcome");
		const ratingIndex = steps.findIndex((step) => step.page.type === "rating");
		const insertAfterIndex = welcomeIndex >= 0 ? welcomeIndex : ratingIndex;
		const questionPageRef =
			(welcomeIndex >= 0 ? steps[welcomeIndex]?.page : undefined) ||
			(ratingIndex >= 0 ? steps[ratingIndex]?.page : undefined) ||
			steps[0]?.page;

		if (questionPageRef) {
			const questionSteps: FlowStep[] = questions.map((question) => ({
				page: questionPageRef,
				questionId: question.id,
				isQuestionStep: true,
			}));
			const insertAt = insertAfterIndex >= 0 ? insertAfterIndex + 1 : 0;
			steps.splice(insertAt, 0, ...questionSteps);
		}
	}

	return steps;
}

function clearQuestionMediaState(
	questionAnswers: Record<string, QuestionAnswer>,
): Record<string, QuestionAnswer> {
	return Object.fromEntries(
		Object.entries(questionAnswers).map(([questionId, answer]) => [
			questionId,
			{
				...answer,
				videoFile: null,
				videoPreview: null,
				audioFile: null,
				audioPreview: null,
			},
		]),
	);
}

export const useFeedbackFlowStore = create<FeedbackFlowState>((set, get) => ({
	// Session identifiers
	formSlug: null,
	reviewId: null,

	// Flow state
	currentFlowType: null,
	flowSteps: [],
	currentStepIndex: 0,
	currentQuestionIndex: 0,

	// Data state
	data: initialData,
	isDirty: false,
	isComplete: false,
	isExpired: false,

	// API state
	isSubmitting: false,
	submitError: null,

	// Data setters
	setRating: (rating: number) => {
		const formConfigStore = useFormConfigStore.getState();
		const threshold = formConfigStore.getRatingThreshold();
		const pages = formConfigStore.getPages();
		const questions = formConfigStore.getQuestions();

		const flowType: FlowType =
			rating >= threshold ? "positive" : "negative";
		const flowSteps = buildFlowSteps(pages, questions, flowType);

		set((state) => ({
			data: { ...state.data, rating },
			currentFlowType: flowType,
			flowSteps,
			isDirty: true,
		}));
	},

	setContextText: (text: string) => {
		set((state) => ({
			data: { ...state.data, contentText: text },
			isDirty: true,
		}));
	},

	setConsent: (consent: ConsentType) => {
		set((state) => ({
			data: { ...state.data, consent },
			isDirty: true,
		}));
	},

	setUserInfo: (info: Partial<UserInfo>) => {
		set((state) => ({
			data: {
				...state.data,
				userInfo: { ...state.data.userInfo, ...info },
			},
			isDirty: true,
		}));
	},

	setCompanyInfo: (info: Partial<CompanyInfo>) => {
		set((state) => ({
			data: {
				...state.data,
				companyInfo: { ...state.data.companyInfo, ...info },
			},
			isDirty: true,
		}));
	},

	setQuestionAnswer: (
		questionId: string,
		answer: Partial<QuestionAnswer>,
	) => {
		set((state) => {
			const existingAnswer = state.data.questionAnswers[questionId] || {
				questionId,
				type: "text" as const,
				textContent: "",
				videoFile: null,
				videoPreview: null,
				audioFile: null,
				audioPreview: null,
			};

			return {
				data: {
					...state.data,
					questionAnswers: {
						...state.data.questionAnswers,
						[questionId]: { ...existingAnswer, ...answer },
					},
				},
				isDirty: true,
			};
		});
	},

	// Navigation
	nextStep: () => {
		const { currentStepIndex, flowSteps } = get();
		if (currentStepIndex < flowSteps.length - 1) {
			const nextIndex = currentStepIndex + 1;
			const nextStep = flowSteps[nextIndex];

			// Track question index for question steps
			let questionIndex = 0;
			if (nextStep?.isQuestionStep) {
				const firstQuestionIdx = flowSteps.findIndex((s) => s.isQuestionStep);
				questionIndex = nextIndex - firstQuestionIdx;
			}

			set({
				currentStepIndex: nextIndex,
				currentQuestionIndex: nextStep?.isQuestionStep
					? questionIndex
					: get().currentQuestionIndex,
			});
		}
	},

	prevStep: () => {
		const { currentStepIndex } = get();
		if (currentStepIndex > 0) {
			set({ currentStepIndex: currentStepIndex - 1 });
		}
	},

	canGoBack: () => {
		const { currentStepIndex, flowSteps } = get();
		const currentStep = flowSteps[currentStepIndex];
		return (
			currentStepIndex > 0 &&
			currentStep?.page.type !== "thank_you" &&
			currentStep?.page.type !== "negative_thank_you"
		);
	},

	canProceed: () => {
		const { flowSteps, currentStepIndex, data } = get();
		const currentStep = flowSteps[currentStepIndex];

		if (!currentStep) return false;

		// Handle question steps before the switch
		if (currentStep.isQuestionStep) {
			const questionId = currentStep.questionId;
			if (!questionId) return true;
			const answer = data.questionAnswers[questionId];
			if (!answer) return false;
			if (answer.type === "text") {
				return answer.textContent.trim().length > 0;
			}
			if (answer.type === "audio") {
				return answer.audioFile !== null || !!answer.audioUrl;
			}
			return answer.videoFile !== null || !!answer.videoUrl;
		}

		const { page } = currentStep;

		switch (page.type) {
			case "rating":
				return data.rating >= 1 && data.rating <= 5;

			case "private_feedback":
				return data.userInfo.customer_name.trim().length > 0;

			case "welcome":
				return true;

			case "consent":
				return data.consent !== null;

			case "about_user":
				return (
					data.userInfo.customer_name.trim().length > 0 &&
					data.userInfo.customer_email.trim().length > 0
				);

			case "about_company":
				return true; // Component handles its own validation

			case "thank_you":
			case "negative_thank_you":
				return false; // End of flow

			default:
				return false;
		}
	},

	getCurrentStep: () => {
		const { flowSteps, currentStepIndex } = get();
		return flowSteps[currentStepIndex] ?? null;
	},

	// API actions
	initializeFlow: (formSlug: string) => {
		const formConfigStore = useFormConfigStore.getState();
		const pages = formConfigStore.getPages();

		// Start with just the rating page
		const ratingPage = pages.find((p) => p.type === "rating");
		const initialSteps: FlowStep[] = ratingPage
			? [{ page: ratingPage }]
			: [];

		set({
			formSlug,
			reviewId: null,
			currentFlowType: null,
			flowSteps: initialSteps,
			currentStepIndex: 0,
			currentQuestionIndex: 0,
			data: initialData,
			isDirty: false,
			isComplete: false,
			isSubmitting: false,
			submitError: null,
		});
	},

	createSubmission: async () => {
		const { formSlug, data } = get();
		if (!formSlug) throw new Error("Form ID not set");

		set({ isSubmitting: true, submitError: null });
		try {
			const response = await feedbackApi.createSubmission(formSlug, {
				rating: data.rating,
			});
			set({
				reviewId: response.data.id,
				isSubmitting: false,
			});
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Failed to create submission";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	saveCurrentStepData: async () => {
		const { formSlug, reviewId, currentFlowType, data } = get();
		if (!formSlug || !reviewId) return;

		// Positive flow data is saved incrementally in each step
		if (currentFlowType === "positive") return;

		set({ isSubmitting: true, submitError: null });
		try {
			// Negative flow: send private feedback and customer info
			const payload = {
				content_text: data.contentText,
				customer_name: data.userInfo.customer_name,
				customer_email: data.userInfo.customer_email || undefined,
				customer_phone: data.userInfo.customer_phone || undefined,
			};

			await feedbackApi.updateSubmission(formSlug, reviewId, payload);

			// Commit the submission to mark as complete
			await feedbackApi.commitSubmission(formSlug, reviewId);

			set((state) => ({
				isSubmitting: false,
				isDirty: false,
				isComplete: true,
				data: {
					...state.data,
					questionAnswers: clearQuestionMediaState(
						state.data.questionAnswers,
					),
				},
			}));
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to save data";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	hasPendingUploads: () => {
		const { data } = get();
		if (data.userInfo.photoFile) return true;
		if (data.companyInfo.logoFile) return true;
		for (const answer of Object.values(data.questionAnswers)) {
			if (answer.type === "video" && answer.videoFile && !answer.videoUrl) return true;
			if (answer.type === "audio" && answer.audioFile && !answer.audioUrl) return true;
		}
		return false;
	},

	uploadAllFiles: async () => {
		const { formSlug, reviewId, data } = get();
		if (!formSlug || !reviewId) return;

		set({ isSubmitting: true, submitError: null });
		try {
			// Upload user photo if available
			if (data.userInfo.photoFile) {
				await feedbackApi.uploadCustomerAvatar(
					formSlug,
					reviewId,
					data.userInfo.photoFile,
				);
				set((state) => ({
					data: {
						...state.data,
						userInfo: { ...state.data.userInfo, photoFile: null },
					},
				}));
			}

			// Upload company logo if available
			if (data.companyInfo.logoFile) {
				await feedbackApi.uploadCompanyLogo(
					formSlug,
					reviewId,
					data.companyInfo.logoFile,
				);
				set((state) => ({
					data: {
						...state.data,
						companyInfo: {
							...state.data.companyInfo,
							logoFile: null,
						},
					},
				}));
			}

			// Upload question answers (videos, audio, and text)
			for (const answer of Object.values(data.questionAnswers)) {
				if (answer.type === "video" && answer.videoFile && !answer.videoUrl) {
					const response = await feedbackApi.uploadVideo(
						formSlug,
						reviewId,
						answer.questionId,
					);
					await feedbackApi.uploadVideoToMux(
						response.data.upload_url,
						answer.videoFile,
					);
					set((state) => ({
						data: {
							...state.data,
							questionAnswers: {
									...state.data.questionAnswers,
									[answer.questionId]: {
										...state.data.questionAnswers[answer.questionId],
										videoUrl: response.data.upload_url,
									},
								},
							},
					}));
				} else if (answer.type === "audio" && answer.audioFile && !answer.audioUrl) {
					const response = await feedbackApi.uploadAudio(
						formSlug,
						reviewId,
						answer.questionId,
						answer.audioFile,
						answer.audioDuration,
					);
					set((state) => ({
						data: {
							...state.data,
							questionAnswers: {
									...state.data.questionAnswers,
									[answer.questionId]: {
										...state.data.questionAnswers[answer.questionId],
										audioUrl: response.data.audio_url,
									},
								},
							},
					}));
				} else if (answer.type === "text" && answer.textContent) {
					// Submit text answer using the new endpoint
					await feedbackApi.submitTextQuestion(
						formSlug,
						reviewId,
						answer.questionId,
						answer.textContent,
					);
				}
			}

			// Submit user and company info
			const updatedData = get().data;
			const payload = {
				customer_name: updatedData.userInfo.customer_name,
				customer_email: updatedData.userInfo.customer_email,
				customer_phone: updatedData.userInfo.customer_phone || undefined,
				customer_headline: updatedData.userInfo.customer_headline || undefined,
				customer_job_title: updatedData.companyInfo.customer_job_title || undefined,
				customer_company_name: updatedData.companyInfo.customer_company_name || undefined,
				customer_company_website_url: updatedData.companyInfo.customer_company_website_url || undefined,
				consent: updatedData.consent || undefined,
			};

			await feedbackApi.updateSubmission(formSlug, reviewId, payload);
			set({ isSubmitting: false, isDirty: false });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to upload";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	uploadVideo: async (questionId: string, file: File) => {
		const { formSlug, reviewId } = get();
		if (!formSlug || !reviewId) throw new Error("Submission not created");

		const response = await feedbackApi.uploadVideo(
			formSlug,
			reviewId,
			questionId,
		);
		await feedbackApi.uploadVideoToMux(response.data.upload_url, file);
		return response.data.upload_url;
	},

	uploadAudio: async (questionId: string, file: File, audioDuration?: number) => {
		const { formSlug, reviewId } = get();
		if (!formSlug || !reviewId) throw new Error("Submission not created");

		const response = await feedbackApi.uploadAudio(
			formSlug,
			reviewId,
			questionId,
			file,
			audioDuration,
		);
		return response.data.audio_url;
	},

	// Incremental submission methods
	createSubmissionFromRating: async () => {
		const { formSlug, reviewId, data } = get();
		if (!formSlug) throw new Error("Form ID not set");

		set({ isSubmitting: true, submitError: null });
		try {
			// If reviewId already exists, update the rating instead of creating new submission
			if (reviewId) {
				await feedbackApi.updateSubmission(formSlug, reviewId, {
					rating: data.rating,
				});
				set({ isSubmitting: false });
				return reviewId;
			}

			// Create new submission only if reviewId doesn't exist
			const response = await feedbackApi.createSubmission(formSlug, {
				rating: data.rating,
			});
			set({
				reviewId: response.data.id,
				isSubmitting: false,
			});
			return response.data.id;
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Failed to create submission";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	updateQuestionAnswerStep: async (questionId: string) => {
		const { formSlug, reviewId, data } = get();
		if (!formSlug || !reviewId) throw new Error("Submission not created");

		const answer = data.questionAnswers[questionId];
		if (!answer) throw new Error("Answer not found");

		set({ isSubmitting: true, submitError: null });
		try {
			// Handle different answer types
			if (answer.type === "video" && answer.videoFile && !answer.videoUrl) {
				const response = await feedbackApi.uploadVideo(
					formSlug,
					reviewId,
					questionId,
				);
				await feedbackApi.uploadVideoToMux(
					response.data.upload_url,
					answer.videoFile,
				);
				// Update state to store the uploaded URL (keep local file for preview)
				set((state) => ({
					data: {
						...state.data,
						questionAnswers: {
							...state.data.questionAnswers,
							[questionId]: {
								...state.data.questionAnswers[questionId],
								videoUrl: response.data.upload_url,
							},
						},
					},
				}));
			} else if (answer.type === "audio" && answer.audioFile && !answer.audioUrl) {
				const response = await feedbackApi.uploadAudio(
					formSlug,
					reviewId,
					questionId,
					answer.audioFile,
					answer.audioDuration,
				);
				// Update state to store the uploaded URL (keep local file for preview)
				set((state) => ({
					data: {
						...state.data,
						questionAnswers: {
							...state.data.questionAnswers,
							[questionId]: {
								...state.data.questionAnswers[questionId],
								audioUrl: response.data.audio_url,
							},
						},
					},
				}));
			} else if (answer.type === "text" && answer.textContent) {
				// Submit text answer using the new endpoint
				await feedbackApi.submitTextQuestion(
					formSlug,
					reviewId,
					questionId,
					answer.textContent,
				);
			}

			set({ isSubmitting: false });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to save answer";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	updateUserInfoStep: async () => {
		const { formSlug, reviewId, data } = get();
		if (!formSlug || !reviewId) throw new Error("Submission not created");

		set({ isSubmitting: true, submitError: null });
		try {
			// Upload photo if available
			if (data.userInfo.photoFile) {
				await feedbackApi.uploadCustomerAvatar(
					formSlug,
					reviewId,
					data.userInfo.photoFile,
				);
				// Clear file from state
				set((state) => ({
					data: {
						...state.data,
						userInfo: { ...state.data.userInfo, photoFile: null },
					},
				}));
			}

			// Submit user info
			const payload = {
				customer_name: data.userInfo.customer_name,
				customer_email: data.userInfo.customer_email,
				customer_phone: data.userInfo.customer_phone || undefined,
				customer_headline: data.userInfo.customer_headline || undefined,
			};

			await feedbackApi.updateSubmission(formSlug, reviewId, payload);
			set({ isSubmitting: false });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to save user info";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	updateCompanyInfoStep: async () => {
		const { formSlug, reviewId, data } = get();
		if (!formSlug || !reviewId) throw new Error("Submission not created");

		set({ isSubmitting: true, submitError: null });
		try {
			// Upload logo if available
			if (data.companyInfo.logoFile) {
				await feedbackApi.uploadCompanyLogo(
					formSlug,
					reviewId,
					data.companyInfo.logoFile,
				);
				// Clear file from state
				set((state) => ({
					data: {
						...state.data,
						companyInfo: {
							...state.data.companyInfo,
							logoFile: null,
						},
					},
				}));
			}

			// Submit company info
			const payload = {
				customer_job_title: data.companyInfo.customer_job_title || undefined,
				customer_company_name: data.companyInfo.customer_company_name || undefined,
				customer_company_website_url: data.companyInfo.customer_company_website_url || undefined,
			};

			await feedbackApi.updateSubmission(formSlug, reviewId, payload);
			set({ isSubmitting: false });
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Failed to save company info";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	updateConsentAndComplete: async () => {
		const { formSlug, reviewId, data } = get();
		if (!formSlug || !reviewId) throw new Error("Submission not created");

		set({ isSubmitting: true, submitError: null });
		try {
			// Submit consent
			const payload = {
				consent: data.consent || undefined,
			};

			await feedbackApi.updateSubmission(formSlug, reviewId, payload);

			// Commit the submission to mark as complete
			await feedbackApi.commitSubmission(formSlug, reviewId);

			set((state) => ({
				isSubmitting: false,
				isComplete: true,
				isDirty: false,
				data: {
					...state.data,
					questionAnswers: clearQuestionMediaState(
						state.data.questionAnswers,
					),
				},
			}));
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Failed to finalize submission";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	finalizeSubmission: async () => {
		const { formSlug, reviewId } = get();
		if (!formSlug || !reviewId) throw new Error("Submission not created");

		set({ isSubmitting: true, submitError: null });
		try {
			// Commit the submission to mark as complete
			await feedbackApi.commitSubmission(formSlug, reviewId);
			set((state) => ({
				isSubmitting: false,
				isComplete: true,
				isDirty: false,
				data: {
					...state.data,
					questionAnswers: clearQuestionMediaState(
						state.data.questionAnswers,
					),
				},
			}));
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "Failed to finalize submission";
			set({ isSubmitting: false, submitError: message });
			throw error;
		}
	},

	// Flow control
	reset: () => {
		set({
			formSlug: null,
			reviewId: null,
			currentFlowType: null,
			flowSteps: [],
			currentStepIndex: 0,
			currentQuestionIndex: 0,
			data: initialData,
			isDirty: false,
			isComplete: false,
			isExpired: false,
			isSubmitting: false,
			submitError: null,
		});
	},

	complete: () => {
		set({ isComplete: true, isDirty: false });
	},

	setExpired: (expired: boolean) => {
		set({ isExpired: expired });
	},
}));
